import type { LocalDate } from '@shared/calendar';
import type { ProjectShiftDto } from '@shared/shifts';
import type { ProjectConfig } from '@/types/settings';

export interface Project {
  id: string;
  name: string;
  color: string;
  status: 'active' | 'archived';
}

export interface PersonalProject extends Project {
  ownSeconds: number;
  /** The caller's own entry count on the project; ranks the entry form picker (sprint 3). */
  ownEntryCount: number;
  /** Sprint 11 (WP-10). */
  rules: EntryRules;
  /** Sprint 13 (§5.3): shift mode, or null when off. Shift projects are kept out of time pickers. */
  shift?: ProjectShiftDto | null;
}

/** Sprint 13: a shift project is not loggable for time (timer, entries, default project). */
export function isShiftProject(p: { shift?: ProjectShiftDto | null }): boolean {
  return p.shift?.enabled === true;
}

export interface EntryRules {
  manualEntriesAllowed: boolean;
  selfEditAllowed: boolean;
}

/** GET /api/projects (sprint 11): loggable projects plus the rules for No project. */
export interface PersonalProjects {
  items: PersonalProject[];
  noProject: { rules: EntryRules };
}

export interface AdminProject extends Project {
  archivedAt: string | null;
  createdAt: string;
  teamSeconds: number;
  entryCount: number;
  /** Sprint 11 (§8.4). */
  config: ProjectConfig;
}

export interface Entry {
  id: string;
  date: LocalDate;
  startSecond: number;
  durationSeconds: number;
  endDate: LocalDate;
  endSecond: number;
  projectId: string | null;
  description: string | null;
  origin: 'manual' | 'timer';
  /** Sprint 13 (§12): set on derived shift entries (read-only, "Shift" badge). Absent on GET /api/entries/:id. */
  shiftId?: string | null;
}

export interface AdminEntry extends Entry {
  user: { id: string; name: string; status: 'active' | 'deactivated' };
}

export interface EntryPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface PersonalProjectDetail {
  project: Project;
  totals: { ownSeconds: number; entryCount: number };
}

export interface AdminProjectDetail {
  project: AdminProject;
  perUser: Array<{
    user: { id: string; name: string; email: string; status: 'active' | 'deactivated' };
    seconds: number;
    entryCount: number;
  }>;
}
